import { useQuery } from '@tanstack/react-query'
import { BuildingOfficeIcon, PlusIcon } from '@heroicons/react/24/outline'
import api from '../services/api'

interface Client {
  id: string
  client_code: string
  name: string
  industry?: string
  project_count: number
  total_revenue: number
  is_active: boolean
}

export default function Clients() {
  const { data: clients, isLoading } = useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await api.get('/clients')
      return response.data
    }
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your client relationships and projects
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button className="btn btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            New Client
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {clients?.map((client) => (
          <div key={client.id} className="card p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <BuildingOfficeIcon className="h-10 w-10 text-primary-600" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {client.name}
                  </h3>
                  <p className="text-sm text-gray-500">{client.client_code}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                client.is_active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {client.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="mt-4 space-y-2">
              {client.industry && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Industry:</span> {client.industry}
                </p>
              )}
              <p className="text-sm text-gray-600">
                <span className="font-medium">Projects:</span> {client.project_count}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Revenue:</span> �{client.total_revenue?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        ))}
      </div>

      {clients?.length === 0 && (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No clients</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding your first client.
          </p>
        </div>
      )}
    </div>
  )
}